import { Dispatch } from "redux";
import { appConstants } from "../../../action-constants/inapp.constants";
import { handleRequestErrors, getUserIdFromStorage } from "../../../../shared/utils";
import { lightspeedService } from "../../../../services/lightspeedService";
import { 
  LightspeedOAuthSession,
  LightspeedOAuthCallback,
  LightspeedConnection,
  LightspeedConnectionStatus,
  LightspeedInstallation,
  LightspeedApiResponse,
  LightspeedConnectionStatusPayload
} from "../../../../shared/types/lightspeed.types";
import { handleLightspeedError, LightspeedError } from "../../../../shared/utils/lightspeedErrorHandler";

export interface LightspeedStartOAuthPayload {
  storeId?: string;
  redirectUri?: string;
  domainPrefix?: string;
}

export interface LightspeedCallbackPayload extends LightspeedOAuthCallback {}

export interface LightspeedConnectionPayload {
  storeId: string;
}

export interface LightspeedDisconnectPayload {
  storeId: string;
}

export interface LightspeedInstallationPayload {
  sessionId: string;
}

export const StartLightspeedOAuth = (
  actionPayload: LightspeedStartOAuthPayload | "CLEAR"
) => {
  if (actionPayload !== "CLEAR") {
    return (dispatch: Dispatch) => {
      dispatch(request(actionPayload));
      
      const userId = getUserIdFromStorage();
      
      return lightspeedService.startOAuth(actionPayload.storeId, userId || undefined, actionPayload.domainPrefix)
        .then((response: LightspeedApiResponse<LightspeedOAuthSession>) => {
          dispatch(success(response));
          if (response.success && response.data?.authorizationUrl) {
            const rawUrl = (response.data as any).authorizationUrl as string | undefined;
            const authUrl = typeof rawUrl === 'string'
              ? rawUrl.trim().replace(/^`+|`+$/g, '').replace(/^"+|"+$/g, '')
              : undefined;
            if (authUrl) {
              window.open(authUrl, '_blank', 'noopener,noreferrer');
            }
          }
          return response;
        })
        .catch((error: LightspeedError) => {
          const lightspeedError = handleLightspeedError(error);
          dispatch(failure(lightspeedError));
          throw lightspeedError;
        });
    };
  }

  return (dispatch: Dispatch) => {
    dispatch(clear());
  };

  function request(params: LightspeedStartOAuthPayload) {
    return { type: appConstants.LIGHTSPEED_START_OAUTH_PENDING, params };
  }
  function success(response: LightspeedApiResponse<LightspeedOAuthSession>) {
    return { type: appConstants.LIGHTSPEED_START_OAUTH_SUCCESS, response };
  }
  function failure(error: LightspeedError) {
    return { type: appConstants.LIGHTSPEED_START_OAUTH_FAILURE, error };
  }
  function clear() {
    return { type: appConstants.LIGHTSPEED_START_OAUTH_RESET, clear_data: "" };
  }
};

export const HandleLightspeedCallback = (
  actionPayload: LightspeedCallbackPayload | "CLEAR"
) => {
  if (actionPayload !== "CLEAR") {
    return (dispatch: Dispatch) => {
      dispatch(request(actionPayload));
      
      return lightspeedService.handleCallback(actionPayload)
        .then((response: LightspeedApiResponse<LightspeedConnection>) => {
          dispatch(success(response));
          return response;
        })
        .catch((error: LightspeedError) => {
          const lightspeedError = handleLightspeedError(error);
          dispatch(failure(lightspeedError));
          throw lightspeedError;
        });
    };
  }

  return (dispatch: Dispatch) => {
    dispatch(clear());
  };

  function request(params: LightspeedCallbackPayload) {
    return { type: appConstants.LIGHTSPEED_HANDLE_CALLBACK_PENDING, params };
  }
  function success(response: LightspeedApiResponse<LightspeedConnection>) {
    return { type: appConstants.LIGHTSPEED_HANDLE_CALLBACK_SUCCESS, response };
  }
  function failure(error: LightspeedError) {
    return { type: appConstants.LIGHTSPEED_HANDLE_CALLBACK_FAILURE, error };
  }
  function clear() {
    return { type: appConstants.LIGHTSPEED_HANDLE_CALLBACK_RESET, clear_data: "" };
  }
};

export const CheckLightspeedConnection = (
  actionPayload: LightspeedConnectionPayload | "CLEAR"
) => {
  if (actionPayload !== "CLEAR") {
    return (dispatch: Dispatch) => {
      dispatch(request(actionPayload));
      
      return lightspeedService.checkConnectionStatus(actionPayload.storeId)
        .then((response) => {
          dispatch(success(response));
          return response;
        })
        .catch((error: LightspeedError) => {
          const lightspeedError = handleLightspeedError(error);
          dispatch(failure(lightspeedError));
          throw lightspeedError;
        });
    };
  }

  return (dispatch: Dispatch) => {
    dispatch(clear());
  };

  function request(params: LightspeedConnectionPayload) {
    return { type: appConstants.LIGHTSPEED_CHECK_CONNECTION_PENDING, params };
  }
  function success(response: LightspeedApiResponse<LightspeedConnectionStatusPayload | null>) {
    return { type: appConstants.LIGHTSPEED_CHECK_CONNECTION_SUCCESS, response };
  }
  function failure(error: LightspeedError) {
    return { type: appConstants.LIGHTSPEED_CHECK_CONNECTION_FAILURE, error };
  }
  function clear() {
    return { type: appConstants.LIGHTSPEED_CHECK_CONNECTION_RESET, clear_data: "" };
  }
};

export const DisconnectLightspeedStore = (
  actionPayload: LightspeedDisconnectPayload | "CLEAR"
) => {
  if (actionPayload !== "CLEAR") {
    return (dispatch: Dispatch) => {
      dispatch(request(actionPayload));
      
      return lightspeedService.disconnectStore(actionPayload.storeId)
        .then((response) => {
          dispatch(success(response));
          return response;
        })
        .catch((error: LightspeedError) => {
          const lightspeedError = handleLightspeedError(error);
          dispatch(failure(lightspeedError));
          throw lightspeedError;
        });
    };
  }

  return (dispatch: Dispatch) => {
    dispatch(clear());
  };

  function request(params: LightspeedDisconnectPayload) {
    return { type: appConstants.LIGHTSPEED_DISCONNECT_PENDING, params };
  }
  function success(response: LightspeedApiResponse<{ success: boolean }>) {
    return { type: appConstants.LIGHTSPEED_DISCONNECT_SUCCESS, response };
  }
  function failure(error: LightspeedError) {
    return { type: appConstants.LIGHTSPEED_DISCONNECT_FAILURE, error };
  }
  function clear() {
    return { type: appConstants.LIGHTSPEED_DISCONNECT_RESET, clear_data: "" };
  }
};

export const CheckLightspeedInstallation = (
  actionPayload: LightspeedInstallationPayload | "CLEAR"
) => {
  if (actionPayload !== "CLEAR") {
    return (dispatch: Dispatch) => {
      dispatch(request(actionPayload));
      
      return lightspeedService.checkInstallationStatus(actionPayload.sessionId)
        .then((response: LightspeedApiResponse<LightspeedInstallation>) => {
          dispatch(success(response));
          return response;
        })
        .catch((error: LightspeedError) => {
          const lightspeedError = handleLightspeedError(error);
          dispatch(failure(lightspeedError));
          throw lightspeedError;
        });
    };
  }

  return (dispatch: Dispatch) => {
    dispatch(clear());
  };

  function request(params: LightspeedInstallationPayload) {
    return { type: appConstants.LIGHTSPEED_CHECK_INSTALLATION_PENDING, params };
  }
  function success(response: LightspeedApiResponse<LightspeedInstallation>) {
    return { type: appConstants.LIGHTSPEED_CHECK_INSTALLATION_SUCCESS, response };
  }
  function failure(error: LightspeedError) {
    return { type: appConstants.LIGHTSPEED_CHECK_INSTALLATION_FAILURE, error };
  }
  function clear() {
    return { type: appConstants.LIGHTSPEED_CHECK_INSTALLATION_RESET, clear_data: "" };
  }
};

export const lightspeedActions = {
  StartLightspeedOAuth,
  HandleLightspeedCallback,
  CheckLightspeedConnection,
  DisconnectLightspeedStore,
  CheckLightspeedInstallation,
};